import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { currentSession, signOut } from 'aws-auth-lib';
import { useQueryClient } from '@tanstack/react-query';
import { getUser, setPanelRole } from '../services/api';
import { portalConfig } from '../configs/portal';
import { mergeOrganizationAccount } from '../utils/organization';
import {
    ensureAuthSuccess, getAllowedRoles, getAvailableClinics,
    readStoredAccess, resolvePortalAccess, storeAccess
} from '../utils/access';

const AuthContext = createContext(null);
const guestState = { status: 'guest', user: null, role: null };
const userQueryKey = ['portal-user', portalConfig.id];

export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const [access, setAccess] = useState({ ...guestState, status: 'loading' });
    const accessRef = useRef(access);
    accessRef.current = access;
    const generation = useRef(0);
    const lastUserUuid = useRef(null);

    const applyUser = useCallback((user, preferred) => {
        const next = resolvePortalAccess(user, preferred);
        lastUserUuid.current = user.uuid;
        storeAccess(user.uuid, next.role);
        setPanelRole(next.role?.uuid);
        setAccess(next);
        return next;
    }, []);

    const clearAccess = useCallback(() => {
        generation.current += 1;
        storeAccess(lastUserUuid.current, null);
        lastUserUuid.current = null;
        setPanelRole(null);
        queryClient.clear();
        setAccess(guestState);
    }, [queryClient]);

    const loadAccess = useCallback(async () => {
        const request = ++generation.current;
        setPanelRole(null);
        await queryClient.cancelQueries();
        if (request !== generation.current) return;
        queryClient.clear();
        setAccess({ ...guestState, status: 'loading' });
        try {
            const session = ensureAuthSuccess(await currentSession());
            if (request !== generation.current) return;
            if (!session?.tokens?.idToken) {
                setAccess(guestState);
                return;
            }
            const user = await queryClient.fetchQuery({
                queryKey: userQueryKey, queryFn: getUser, retry: false, staleTime: 0
            });
            if (request !== generation.current) return;
            applyUser(user, readStoredAccess(user.uuid));
        } catch {
            if (request !== generation.current) return;
            setPanelRole(null);
            queryClient.clear();
            setAccess({ ...guestState, status: 'error' });
        }
    }, [applyUser, queryClient]);

    useEffect(() => {
        void loadAccess();
        return () => {
            generation.current += 1;
            setPanelRole(null);
        };
    }, [loadAccess]);

    const selectAccess = useCallback(async (roleUuid, clinicUuid) => {
        const current = accessRef.current;
        const role = getAllowedRoles(current.user).find((candidate) =>
            candidate.uuid === roleUuid && candidate.account.uuid === clinicUuid
        );
        if (!role || !['ready', 'select-access'].includes(current.status)) {
            throw new Error('INVALID_ACCESS_SELECTION');
        }
        if (current.role?.uuid === role.uuid) return;
        const request = ++generation.current;
        setPanelRole(null);
        setAccess({ ...current, status: 'switching', role: null });
        await queryClient.cancelQueries();
        if (request !== generation.current) throw new Error('SESSION_CHANGED');
        queryClient.clear();
        queryClient.setQueryData(userQueryKey, current.user);
        storeAccess(current.user.uuid, role);
        setPanelRole(role.uuid);
        setAccess({ status: 'ready', user: current.user, role });
    }, [queryClient]);

    const refreshUser = useCallback(async () => {
        const request = generation.current;
        const previous = accessRef.current.role;
        const user = await queryClient.fetchQuery({
            queryKey: userQueryKey, queryFn: getUser, retry: false, staleTime: 0
        });
        if (request !== generation.current) throw new Error('SESSION_CHANGED');
        const preferred = previous ? {
            roleUuid: previous.uuid, clinicUuid: previous.account.uuid
        } : readStoredAccess(user.uuid);
        const next = resolvePortalAccess(user, preferred);
        if (next.role?.uuid !== previous?.uuid) {
            const transition = ++generation.current;
            setPanelRole(null);
            await queryClient.cancelQueries();
            if (transition !== generation.current) throw new Error('SESSION_CHANGED');
            queryClient.clear();
        }
        applyUser(user, preferred);
        return user;
    }, [applyUser, queryClient]);

    const updateUserPicture = useCallback((picture, userUuid) => {
        if (!picture || typeof picture !== 'string') throw new Error('PICTURE_UNAVAILABLE');
        setAccess((current) => current.status === 'ready' && current.user?.uuid === userUuid
            ? { ...current, user: { ...current.user, picture } } : current);
        queryClient.setQueryData(userQueryKey, (cached) =>
            cached?.uuid === userUuid ? { ...cached, picture } : cached);
    }, [queryClient]);

    const updateOrganizationAccount = useCallback((patch, roleUuid) => {
        const selected = accessRef.current;
        if (selected.status !== 'ready' || selected.role?.uuid !== roleUuid) return;
        const accountUuid = selected.role.account.uuid;
        const userUuid = selected.user.uuid;
        setAccess((current) => {
            if (current.status !== 'ready' || current.role?.uuid !== roleUuid || current.user?.uuid !== userUuid) return current;
            const user = mergeOrganizationAccount(current.user, accountUuid, patch);
            return { ...current, user, role: user.roles.find((candidate) => candidate.uuid === roleUuid) };
        });
        queryClient.setQueryData(userQueryKey, (cached) => cached?.uuid === userUuid
            ? mergeOrganizationAccount(cached, accountUuid, patch) : cached);
    }, [queryClient]);

    const logout = async () => {
        await signOut();
        clearAccess();
    };

    const roles = getAllowedRoles(access.user);
    return (
        <AuthContext.Provider value={{
            ...access, roles, clinics: getAvailableClinics(roles),
            loadAccess, selectAccess, refreshUser, updateUserPicture, updateOrganizationAccount, logout
        }}>
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };
export const useAuth = () => useContext(AuthContext);
